import { watch } from 'vue';
import { useTheme } from 'vuetify';
import { isAllowlistedFontUrl } from '@/utils/settings-validation';
import { useOSettings } from '@/composables/use-settings';

export const BRANDING_COLOR_MAP = {
  app_color_primary: 'primary',
  app_color_secondary: 'secondary',
  app_color_toolbar: 'toolbar',
  app_color_error: 'error',
  app_color_success: 'success',
  app_color_warning: 'warning',
  app_color_info: 'info',
  app_color_background: 'background',
  app_color_surface: 'surface',
} as const;

let injectedFontLinkElement: HTMLLinkElement | null = null;
let injectedFontUrl: string | null = null;

/** Snapshot of theme-pack colors before branding overrides, keyed by theme name. */
const themeColorBaselines = new Map<string, Record<string, string>>();

function ensureThemeColorBaseline(themeName: string, colors: Record<string, string>) {
  if (!themeColorBaselines.has(themeName)) {
    themeColorBaselines.set(themeName, { ...colors });
  }
}

function writeThemeColor(colors: Record<string, string>, colorToken: string, value: string) {
  try {
    colors[colorToken] = value;
  } catch {
    // Vuetify 4 theme color proxies reject unknown Helix tokens such as `toolbar`.
  }
}

export function applyThemeColorOverride(theme: ReturnType<typeof useTheme>, colorToken: string, value: unknown) {
  if (!colorToken || !theme?.themes?.value || !theme?.global?.name) {
    return;
  }

  const activeThemeName = theme.global.name.value;
  const themeDefinition = theme.themes.value[activeThemeName];

  if (!themeDefinition?.colors) {
    return;
  }

  const colors = themeDefinition.colors as Record<string, string>;
  ensureThemeColorBaseline(activeThemeName, colors);
  const baseline = themeColorBaselines.get(activeThemeName);

  if (!value) {
    if (baseline && Object.hasOwn(baseline, colorToken) && Object.hasOwn(colors, colorToken)) {
      writeThemeColor(colors, colorToken, baseline[colorToken]);
    }
    return;
  }

  writeThemeColor(colors, colorToken, value as string);
}

export function removeFontLink({ head = typeof document !== 'undefined' ? document.head : null }: { head?: ParentNode | null } = {}) {
  if (injectedFontLinkElement?.parentNode) {
    injectedFontLinkElement.parentNode.removeChild(injectedFontLinkElement);
  }

  injectedFontLinkElement = null;
  injectedFontUrl = null;

  if (!head) {
    return;
  }
}

export function injectFontLink(
  url: string | null | undefined,
  {
    head = typeof document !== 'undefined' ? document.head : null,
    validate = isAllowlistedFontUrl,
  }: { head?: ParentNode | null; validate?: (value: unknown) => value is string } = {},
) {
  if (!url) {
    removeFontLink({ head });
    return;
  }

  if (!validate(url) || !head) {
    return;
  }

  if (injectedFontUrl === url) {
    return;
  }

  removeFontLink({ head });

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.dataset.brandingFont = 'true';
  head.appendChild(link);
  injectedFontLinkElement = link;
  injectedFontUrl = url;
}

export function applyFontFamily(
  value: string | null | undefined,
  { root = typeof document !== 'undefined' ? document.documentElement : null }: { root?: HTMLElement | null } = {},
) {
  if (!root) {
    return;
  }

  if (value) {
    root.style.setProperty('--app-font-family', value);
    return;
  }

  root.style.removeProperty('--app-font-family');
}

export function resetBrandingFontLinkState() {
  injectedFontLinkElement = null;
  injectedFontUrl = null;
}

export function resetBrandingColorBaselines() {
  themeColorBaselines.clear();
}

export function getInjectedFontUrl() {
  return injectedFontUrl;
}

export function useBrandingOverrides() {
  const theme = useTheme();
  const settings = useOSettings();

  const applyAllThemeColors = () => {
    for (const [settingKey, colorToken] of Object.entries(BRANDING_COLOR_MAP)) {
      applyThemeColorOverride(theme, colorToken, settings[settingKey]?.value);
    }
  };

  for (const [settingKey, colorToken] of Object.entries(BRANDING_COLOR_MAP)) {
    watch(
      () => settings[settingKey]?.value,
      (value) => {
        applyThemeColorOverride(theme, colorToken, value);
      },
      { immediate: true },
    );
  }

  watch(
    () => theme.global.name.value,
    () => {
      applyAllThemeColors();
    },
  );

  watch(
    () => settings.app_font_url?.value,
    (value) => injectFontLink(value as string | null),
    { immediate: true },
  );
  watch(
    () => settings.app_font_family?.value,
    (value) => applyFontFamily(value as string | null),
    { immediate: true },
  );
}
